export const SUPPORTED_LOCALES = ["en-AU", "en-US"] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = "en-AU";

const REGION_TO_LOCALE: Record<string, SupportedLocale> = {
  AU: "en-AU",
  US: "en-US",
};

export const isSupportedLocale = (locale: string): locale is SupportedLocale =>
  SUPPORTED_LOCALES.includes(locale as SupportedLocale);

export const resolveSupportedLocale = (
  locale?: string | null,
  regionCode?: string | null,
): SupportedLocale => {
  const normalizedRegionCode = regionCode?.toUpperCase();
  if (normalizedRegionCode && REGION_TO_LOCALE[normalizedRegionCode]) {
    return REGION_TO_LOCALE[normalizedRegionCode];
  }

  if (locale && isSupportedLocale(locale)) {
    return locale;
  }

  const localeRegion = locale?.split("-")[1]?.toUpperCase();
  if (localeRegion && REGION_TO_LOCALE[localeRegion]) {
    return REGION_TO_LOCALE[localeRegion];
  }

  return DEFAULT_LOCALE;
};

const getRequestedLocales = (acceptLanguage?: string | null) =>
  acceptLanguage
    ?.split(",")
    .map((entry, index) => {
      const [locale, ...parameters] = entry.trim().split(";");
      const qValue = parameters
        .map((parameter) => parameter.trim())
        .find((parameter) => parameter.startsWith("q="))
        ?.slice(2);

      const parsedQValue = qValue ? Number.parseFloat(qValue) : 1;

      return {
        index,
        locale,
        q: Number.isFinite(parsedQValue) ? parsedQValue : 0,
      };
    })
    .filter(({ locale, q }) => Boolean(locale) && q > 0)
    .sort((a, b) => b.q - a.q || a.index - b.index) ?? [];

export const resolveCountryCodeFromAcceptLanguage = (
  acceptLanguage?: string | null,
): string | undefined => {
  for (const { locale } of getRequestedLocales(acceptLanguage)) {
    try {
      const region = new Intl.Locale(locale).region?.toUpperCase();
      if (region && /^[A-Z]{2}$/.test(region)) return region;
    } catch {
      // Ignore malformed browser locales and try the next preference.
    }
  }

  return undefined;
};

export const resolveLocaleFromAcceptLanguage = (
  acceptLanguage?: string | null,
): SupportedLocale => {
  for (const { locale: requestedLocale } of getRequestedLocales(
    acceptLanguage,
  )) {
    if (isSupportedLocale(requestedLocale)) {
      return requestedLocale;
    }

    const region = requestedLocale.split("-")[1]?.toUpperCase();
    if (region && REGION_TO_LOCALE[region]) {
      return REGION_TO_LOCALE[region];
    }
  }

  return DEFAULT_LOCALE;
};

export const getCountryCode = (locale: SupportedLocale): "AU" | "US" =>
  locale === "en-US" ? "US" : "AU";

const REGIONAL_COPY = {
  "en-AU": {
    canceled: "cancelled",
    canceledTitle: "Cancelled",
    place: "place",
    places: "places",
    authorization: "authorisation",
    authorizeCard: "Authorise card",
    finalizingPayment: "Finalising your payment",
    phoneNumber: "Mobile number",
    phoneNumberQuestion: "What's your mobile number?",
    invalidPhoneNumber: "Enter a valid mobile number for the selected country.",
    approvalPaymentNotice: "When you send your request, we'll authorise your card. You'll only be charged if the host approves.",
  },
  "en-US": {
    canceled: "canceled",
    canceledTitle: "Canceled",
    place: "spot",
    places: "spots",
    authorization: "authorization",
    authorizeCard: "Authorize card",
    finalizingPayment: "Finalizing your payment",
    phoneNumber: "Phone number",
    phoneNumberQuestion: "What's your phone number?",
    invalidPhoneNumber: "Enter a valid phone number for the selected country.",
    approvalPaymentNotice: "When you send your request, we'll authorize your card. You'll only be charged if the host approves.",
  },
} satisfies Record<
  SupportedLocale,
  {
    canceled: string;
    canceledTitle: string;
    place: string;
    places: string;
    authorization: string;
    authorizeCard: string;
    finalizingPayment: string;
    phoneNumber: string;
    phoneNumberQuestion: string;
    invalidPhoneNumber: string;
    approvalPaymentNotice: string;
  }
>;

export const getRegionalCopy = (
  locale: SupportedLocale = DEFAULT_LOCALE,
) => REGIONAL_COPY[locale];

export const formatPlaceCount = (
  count: number,
  locale: SupportedLocale = DEFAULT_LOCALE,
): string => {
  const copy = getRegionalCopy(locale);
  return `${count} ${count === 1 ? copy.place : copy.places}`;
};

export const formatPlacesLeft = (
  count: number,
  locale: SupportedLocale = DEFAULT_LOCALE,
): string => `${formatPlaceCount(count, locale)} left`;

export const formatOnlyPlacesLeft = (
  count: number,
  locale: SupportedLocale = DEFAULT_LOCALE,
): string => `Only ${formatPlacesLeft(count, locale)}`;
